import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Button, Icon, IconButton, Spinner, StatePanel } from "../components/ui";
import { UserContext } from "../contexts";
import MessageBubble from "./MessageBubble";
import Composer from "./Composer";
import ThreadHeader from "./ThreadHeader";
import ImageLightbox from "./ImageLightbox";
import MeetingDialog from "./MeetingDialog";
import {
  createChannelMeeting,
  markChannelRead,
  useChatThread,
  useRefreshChat,
} from "./useMerchantChat";

/**
 * One open conversation: identity header (laptop), day-separated message
 * list pinned to the newest message unless the reader has scrolled up, a
 * "load older" pager, and the composer. Read receipts post when the newest
 * visible message id advances. `canPost=false` (the owners updates channel)
 * swaps the composer for an announcement notice.
 */

const dayLabel = (iso) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
};

const MessageThread = ({ channel }) => {
  const { messages, isLoading, isError, hasOlder, loadOlder, send, retry } =
    useChatThread(channel?.id);
  const refreshChat = useRefreshChat();
  const { user } = useContext(UserContext) ?? {};
  const scrollRef = useRef(null);
  const pinnedRef = useRef(true);
  const lastReadRef = useRef(0);
  // `pinnedRef` is a ref so scrolling never re-renders the thread; the
  // jump-to-latest control does need a render, so it gets its own state and
  // is only set when the answer actually changes.
  const [scrolledUp, setScrolledUp] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);

  // Only RC master admins can start a meeting: they hold the only Roam seats
  // and Roam requires the meeting host to be a workspace member.
  const canStartMeeting = Boolean(user?.is_master_admin) && Boolean(channel?.is_provisioned);

  const lastRealId = useMemo(() => {
    const real = messages.filter((m) => typeof m.id === "number");
    return real.length ? real[real.length - 1].id : 0;
  }, [messages]);

  // Keep the view pinned to the newest message while the reader is at the
  // bottom; never yank them down while they're reading history.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Read receipt whenever the newest message advances.
  useEffect(() => {
    if (!channel?.id || !lastRealId || lastRealId <= lastReadRef.current) return;
    lastReadRef.current = lastRealId;
    markChannelRead(channel.id, lastRealId).then(refreshChat);
  }, [channel?.id, lastRealId, refreshChat]);

  useEffect(() => {
    // New channel — re-pin and reset the read watermark.
    pinnedRef.current = true;
    lastReadRef.current = 0;
    setScrolledUp(false);
    setLightbox(null);
  }, [channel?.id]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    pinnedRef.current = atBottom;
    setScrolledUp((current) => (current === !atBottom ? current : !atBottom));
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    pinnedRef.current = true;
    setScrolledUp(false);
  };

  if (!channel) {
    return (
      <div className="rc-chat__thread rc-chat__thread--empty">
        <StatePanel
          state="empty"
          icon="MessageCircle"
          title="Pick a conversation"
          description="Choose a channel on the left to start chatting."
        />
      </div>
    );
  }

  const rows = [];
  let previousDay = null;
  let previousAuthor = null;

  messages.forEach((message) => {
    const day = dayLabel(message.created_at);
    if (day !== previousDay) {
      rows.push(
        <div className="rc-chat__day" key={`day-${day}-${message.id}`}>
          <span>{day}</span>
        </div>
      );
      previousDay = day;
      previousAuthor = null;
    }
    const authorKey = message.author?.is_self ? "__self__" : message.author?.name;
    rows.push(
      <MessageBubble
        key={message.id}
        message={message}
        showAuthor={authorKey !== previousAuthor}
        onRetry={retry}
        onOpenImage={(images, index) => setLightbox({ images, index })}
      />
    );
    previousAuthor = authorKey;
  });

  return (
    <div className="rc-chat__thread">
      <ThreadHeader channel={channel} />

      <div className="rc-chat__scroll" ref={scrollRef} onScroll={onScroll}>
        {hasOlder && !isLoading && (
          <div className="rc-chat__older">
            <Button variant="ghost" size="sm" onClick={loadOlder}>
              Load older messages
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="rc-chat__loading">
            <Spinner />
          </div>
        )}

        {isError && !messages.length && (
          <StatePanel state="error" description="Couldn't load this conversation — it will retry automatically." />
        )}

        {!isLoading && !isError && !messages.length && (
          <StatePanel
            state="empty"
            icon="MessageCircle"
            title="No messages yet"
            description={
              channel.can_post
                ? "Say hello — the Retail Circle team reads this channel."
                : "Updates from Retail Circle will appear here."
            }
          />
        )}

        {rows}
      </div>

      {scrolledUp && (
        <IconButton
          className="rc-chat__jump"
          label="Jump to the latest message"
          onClick={jumpToLatest}
        >
          <Icon name="ChevronDown" size="sm" />
        </IconButton>
      )}

      {/* Composer keeps its own short placeholder: the channel name is
          already the page title, and repeating it there overflowed the
          field on mobile. */}
      {channel.can_post ? (
        <Composer
          onSend={(body, files) => send(body, files)}
          onStartMeeting={canStartMeeting ? () => setMeetingOpen(true) : undefined}
        />
      ) : (
        <div className="rc-chat__readonly">
          <Icon name="Bell" size="sm" />
          Announcements from Retail Circle — only the Retail Circle team can post here.
        </div>
      )}

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {meetingOpen && (
        <MeetingDialog
          channelName={channel.name}
          onClose={() => setMeetingOpen(false)}
          onCreate={(payload) =>
            createChannelMeeting(channel.id, payload).then((message) => {
              // Pull the posted link in immediately rather than waiting for
              // the next tail poll — it is the result of a deliberate action,
              // so a visible delay reads as a failure.
              refreshChat();
              return message;
            })
          }
        />
      )}
    </div>
  );
};

export default MessageThread;
